import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { Box } from '@mui/material'
import axAPI from 'httpConfig'
import { IRationArray, IRationVersion } from 'models/IRationSystem'
import moment, { Moment } from 'moment'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { COLORS } from 'styles/colors'

type RationChange = {
	at: Moment
	date: string
	title: string
	rationId: number
	versionId: number
}

type RationChangeNoticeProps = {
	startDate: Moment
	endDate: Moment
	onDays?: (lines: { date: string; titles: string[] }[]) => void
}

const toMoment = (value?: number | string | null) => {
	if (value == null || value === '') return null

	if (typeof value === 'number') {
		return value < 1e12 ? moment.unix(value) : moment(value)
	}

	const numeric = Number(value)
	if (!Number.isNaN(numeric) && String(value).trim() !== '') {
		return numeric < 1e12 ? moment.unix(numeric) : moment(numeric)
	}

	const parsed = moment(value)
	return parsed.isValid() ? parsed : null
}

const orderedVersions = (versions: IRationVersion[]) =>
	[...versions].sort((left, right) => {
		const leftNumber = left.version_number ?? 0
		const rightNumber = right.version_number ?? 0
		if (leftNumber !== rightNumber) return leftNumber - rightNumber
		return (left.created_at ?? 0) - (right.created_at ?? 0)
	})

const collectRationChanges = (rations: IRationArray[]) => {
	const changes: RationChange[] = []

	rations.forEach(ration => {
		if (ration.id == null) return

		const versions = orderedVersions(ration.versions ?? [])
		const activeVersionId = ration.active_version?.id

		versions.forEach((version, index) => {
			if (version.id === activeVersionId) return

			const nextVersion = versions[index + 1]
			const changedAt = toMoment(
				version.archived_at || nextVersion?.created_at || version.created_at,
			)
			if (!changedAt) return

			changes.push({
				at: changedAt,
				date: changedAt.format('DD.MM.YYYY'),
				title: ration.title,
				rationId: ration.id,
				versionId: version.id,
			})
		})
	})

	return changes
}

const RationChangeNotice = ({
	startDate,
	endDate,
	onDays,
}: RationChangeNoticeProps) => {
	const [rations, setRations] = useState<IRationArray[] | null>(null)

	useEffect(() => {
		let cancelled = false

		const load = async () => {
			try {
				const response = await axAPI.get('ration/')
				const list: IRationArray[] = Array.isArray(response.data)
					? response.data
					: []
				const detailed = await Promise.all(
					list.map(async ration => {
						if (ration.versions?.length || ration.id == null) return ration
						try {
							const detail = await axAPI.get(`ration/${ration.id}`)
							return detail.data as IRationArray
						} catch {
							return ration
						}
					}),
				)
				if (!cancelled) setRations(detailed)
			} catch {
				if (!cancelled) setRations([])
			}
		}

		load()
		return () => {
			cancelled = true
		}
	}, [])

	const days = useMemo(() => {
		const changes = collectRationChanges(rations ?? []).filter(change =>
			change.at.isBetween(startDate, endDate, 'day', '[]'),
		)
		const grouped = new Map<string, RationChange[]>()

		changes
			.sort((left, right) => left.at.valueOf() - right.at.valueOf())
			.forEach(change => {
				const sameDay = grouped.get(change.date) ?? []
				const existing = sameDay.findIndex(
					item =>
						item.rationId === change.rationId || item.title === change.title,
				)
				if (existing >= 0) sameDay[existing] = change
				else sameDay.push(change)
				grouped.set(change.date, sameDay)
			})

		return Array.from(grouped.entries())
	}, [endDate, rations, startDate])

	useEffect(() => {
		onDays?.(
			days.map(([date, changes]) => ({
				date,
				titles: changes.map(change => change.title),
			})),
		)
	}, [days, onDays])

	if (!days.length) return null

	return (
		<Box
			sx={{
				display: 'flex',
				alignItems: 'center',
				alignSelf: 'stretch',
				width: '100%',
				height: '72px',
				boxSizing: 'border-box',
				gap: '10px',
				mt: '16px',
				px: '16px',
				py: '16px',
				borderRadius: '12px',
				backgroundColor: COLORS.white,
				boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.06)',
			}}
		>
			<InfoOutlinedIcon
				sx={{ fontSize: 20, color: COLORS.icon, flexShrink: 0 }}
			/>
			<Box
				sx={{
					display: 'flex',
					flexDirection: 'column',
					gap: '2px',
					fontSize: '14px',
					fontWeight: 400,
					lineHeight: '20px',
					color: '#1C1C1E',
				}}
			>
				{days.map(([day, dayChanges]) => (
					<Box key={day} component='p' sx={{ m: 0 }}>
						{day} был изменен{' '}
						{dayChanges.map((change, index) => (
							<Fragment key={`${change.rationId}-${change.versionId}`}>
								{index > 0 && ', '}
								<Box
									component={RouterLink}
									to={`/feed/ration/${change.rationId}/version/${change.versionId}`}
									sx={{
										color: COLORS.main,
										fontSize: '14px',
										fontWeight: 400,
										lineHeight: '20px',
										padding: 0,
										textDecoration: 'underline',
										textUnderlineOffset: '2px',
										'&:hover': {
											color: COLORS.main,
										},
									}}
								>
									{`рацион "${change.title}"`}
								</Box>
							</Fragment>
						))}
						.
					</Box>
				))}
			</Box>
		</Box>
	)
}

export default RationChangeNotice
